// ============================================================================
// Time & Labor — the vocabulary, written down once (coi-state.ts pattern).
// Every helper treats an unrecognised or null value as the UNSAFE answer, so
// a word added upstream fails visibly rather than silently passing.
// ============================================================================

import type {
  CaptureMode,
  ExceptionSeverity,
  ExceptionStatus,
  GeofenceResult,
  SegmentStatus,
} from './types';

export const SEVERITY_ORDER: readonly ExceptionSeverity[] = ['high', 'medium', 'low'];

export function severityLabel(s: string | null | undefined): string {
  switch (s) {
    case 'high': return 'High';
    case 'medium': return 'Medium';
    case 'low': return 'Low';
    default: return 'Unknown severity';
  }
}

/** Badge classes per severity. Unknown renders as the danger style — an
 *  unrecognised severity is a problem, not a neutral. */
export function severityBadgeClass(s: string | null | undefined): string {
  switch (s) {
    case 'high':
      return 'bg-st-danger-bg text-st-danger-fg border-st-danger-border';
    case 'medium':
      return 'bg-st-warn-bg text-st-warn-fg border-st-warn-border';
    case 'low':
      return 'bg-tcs-surface text-tcs-text-secondary border-tcs-border';
    default:
      return 'bg-st-danger-bg text-st-danger-fg border-st-danger-border';
  }
}

export function exceptionStatusLabel(s: string | null | undefined): string {
  switch (s) {
    case 'open': return 'Open';
    case 'reviewed': return 'Reviewed';
    case 'auto_resolved': return 'Auto-handled';
    default: return 'Unknown status';
  }
}

export function segmentStatusLabel(s: string | null | undefined): string {
  switch (s) {
    case 'open': return 'On the clock';
    case 'closed': return 'Closed';
    case 'auto_closed': return 'Auto-closed — awaiting worker';
    case 'worker_resolved': return 'Resolved by worker';
    case 'staff_resolved': return 'Confirmed by HR';
    case 'disputed': return 'Disputed';
    case 'void': return 'Voided';
    default: return 'Unknown';
  }
}

export function segmentStatusBadgeClass(s: string | null | undefined): string {
  switch (s) {
    case 'open':
      return 'bg-st-ok-bg text-st-ok-fg border-st-ok-border';
    case 'closed':
      return 'bg-tcs-surface text-tcs-text-secondary border-tcs-border';
    case 'auto_closed':
      return 'bg-st-warn-bg text-st-warn-fg border-st-warn-border';
    case 'worker_resolved':
      return 'bg-st-ok-bg text-st-ok-fg border-st-ok-border';
    case 'staff_resolved':
      return 'bg-st-ok-bg text-st-ok-fg border-st-ok-border';
    case 'disputed':
      return 'bg-st-info-bg text-st-info-fg border-st-info-border';
    case 'void':
      return 'bg-tcs-surface text-tcs-text-muted border-tcs-border line-through';
    default:
      return 'bg-st-danger-bg text-st-danger-fg border-st-danger-border';
  }
}

/** Worker-facing wording for the location outcome. EVERY value describes an
 *  accepted punch (D-03) — there is deliberately no error wording here. */
export function geofenceResultLabel(r: string | null | undefined): string {
  switch (r) {
    case 'inside': return 'Location confirmed — on site';
    case 'outside': return 'Recorded away from the site — accepted with a flag';
    case 'no_fence': return 'No geofence for this site yet — recorded';
    case 'declined': return 'Location is off — your punch was still recorded';
    case 'unavailable': return "Couldn't get a GPS fix — your punch was still recorded";
    case 'low_accuracy': return "Couldn't confirm the site (weak GPS) — your punch was still recorded";
    case null:
    case undefined:
      return 'Recorded'; // kiosk punches carry no GPS: the device binding is the evidence
    default:
      return 'Recorded';
  }
}

export function geofenceResultChipClass(r: string | null | undefined): string {
  switch (r) {
    case 'inside': return 'bg-st-ok-bg text-st-ok-fg';
    case 'declined': return 'bg-st-warn-bg text-st-warn-fg';
    case 'outside': return 'bg-st-warn-bg text-st-warn-fg';
    default: return 'bg-tcs-surface text-tcs-text-secondary';
  }
}

/**
 * How a punch was made, as a device label — the "Device Used" column of the
 * In/Out Activity report (Buddy Punch says "Mobile Web", "Kiosk"…). Added
 * 2026-10-16: this file had no words for capture modes until then. An unknown
 * mode shows its raw value, so a mode added in SQL is visible, not hidden.
 */
export function captureModeDeviceLabel(m: string | null | undefined): string {
  switch (m) {
    case 'mobile_web': return 'Mobile Web';
    case 'kiosk_pin': return 'Kiosk';
    case 'web_staff': return 'Office Web';
    case 'supervisor_manual': return 'Manager Entry';
    case 'import': return 'Import';
    case 'system_auto_close': return 'Auto-close';
    case 'worker_attested_correction': return 'Worker Correction';
    default: return m ? `Unknown (${m})` : 'Unknown';
  }
}

/**
 * "Changed By Manager" — somebody other than the worker's own clock put this
 * punch there: a supervisor entry, an office (staff web) entry, an attested
 * correction, or any punch written on top of another one. Everything else is
 * "Time Recorded". An unknown mode is NOT assumed to be the worker's own.
 */
export const MANAGER_CHANGE_MODES: readonly CaptureMode[] = ['supervisor_manual', 'web_staff', 'worker_attested_correction'];
export function isChangedByManager(mode: string | null | undefined, supersedesEventId: string | null | undefined): boolean {
  if (supersedesEventId) return true;
  if (mode != null && (MANAGER_CHANGE_MODES as readonly string[]).includes(mode)) return true;
  return !(mode === 'mobile_web' || mode === 'kiosk_pin' || mode === 'import' || mode === 'system_auto_close');
}

export function minutesToHours(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  return (minutes / 60).toFixed(2);
}

/** The complete value lists, exported so the vocabulary test can pin them
 *  against the DB CHECK constraints (a word added in SQL must land here). */
export const ALL_SEVERITIES: readonly ExceptionSeverity[] = ['high', 'medium', 'low'];
export const ALL_EXCEPTION_STATUSES: readonly ExceptionStatus[] = ['open', 'reviewed', 'auto_resolved'];
export const ALL_SEGMENT_STATUSES: readonly SegmentStatus[] = ['open', 'closed', 'auto_closed', 'worker_resolved', 'staff_resolved', 'disputed', 'void'];
export const ALL_GEOFENCE_RESULTS: readonly GeofenceResult[] = ['inside', 'outside', 'no_fence', 'declined', 'unavailable', 'low_accuracy'];
/** punch_events_capture_check, measured 2026-10-16. */
export const ALL_CAPTURE_MODES: readonly CaptureMode[] = ['kiosk_pin', 'mobile_web', 'web_staff', 'supervisor_manual', 'import', 'system_auto_close', 'worker_attested_correction'];
